import React from 'react';
import { Box, Typography, Grid, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import useDashboardData from '../../hooks/useDashboardData';

// Dashboard components
import StatCard from '../../components/admin/dashboard/StatCard';
import RecentPayments from '../../components/admin/dashboard/RecentPayments';
import RecentCustomers from '../../components/admin/dashboard/RecentCustomers';
import QuickActions from '../../components/admin/dashboard/QuickActions';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

/**
 * Admin Dashboard Page
 * Main dashboard view for admin users
 */
const Dashboard = () => {
  const { currentUser } = useAuth();
  const { stats, recentCustomers, recentPayments, loading, error } = useDashboardData();

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Welcome back, {currentUser?.username || 'Admin'}! Here's an overview of your Thailand Real Estate Trip checkout system.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Stats Cards */}
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Total Customers" 
            value={stats.totalCustomers} 
            icon={<PeopleIcon fontSize="large" />} 
            color="#1976d2"
            loading={loading}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Pending Payments" 
            value={stats.pendingPayments} 
            icon={<PaymentIcon fontSize="large" />} 
            color="#f44336"
            loading={loading}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Confirmed Payments" 
            value={stats.confirmedPayments} 
            icon={<CheckCircleIcon fontSize="large" />} 
            color="#4caf50"
            loading={loading}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <StatCard 
            title="Total Revenue" 
            value={stats.totalAmount} 
            icon={<MonetizationOnIcon fontSize="large" />} 
            color="#ff9800"
            loading={loading}
            currency={true}
          />
        </Grid>
        
        {/* Quick Actions */}
        <Grid item xs={12}>
          <QuickActions />
        </Grid>
        
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <RecentPayments 
            payments={recentPayments} 
            loading={loading} 
          />
        </Grid>
        
        {/* Recent Customers */}
        <Grid item xs={12} md={6}>
          <RecentCustomers 
            customers={recentCustomers} 
            loading={loading} 
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;